import { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import MultiSelectAnlagen from "@/components/MultiSelectAnlagen";
import { api, CATEGORY_OPTIONS, STATUS_OPTIONS, FILES_BASE, formatBytes, formatErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, X, Image as ImageIcon, FileText, Save } from "lucide-react";

const emptyForm = {
  title: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
  status: "geplant",
  category: "information",
  anlage_ids: [],
  attachments: [],
};

export default function UpdateFormDialog({ open, onOpenChange, editing, anlagen, onSaved, onAnlagenChanged }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          title: editing.title,
          description: editing.description,
          date: editing.date,
          status: editing.status,
          category: editing.category,
          anlage_ids: editing.anlage_ids || [],
          attachments: editing.attachments || [],
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((f) => ({ ...f, attachments: [...f.attachments, data] }));
      }
      toast.success(`${files.length} Datei(en) hochgeladen`);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.id !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setSubmitting(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      date: form.date,
      status: form.status,
      category: form.category,
      anlage_ids: form.anlage_ids,
      attachment_ids: form.attachments.map((a) => a.id),
    };
    try {
      if (editing) {
        await api.put(`/admin/updates/${editing.id}`, payload);
        toast.success("Update gespeichert");
      } else {
        await api.post("/admin/updates", payload);
        toast.success("Update erstellt");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {editing ? "Update bearbeiten" : "Neues Update"}
          </DialogTitle>
          <DialogDescription>
            Pflegen Sie Informationen, Status und Anhänge. Updates sind öffentlich abrufbar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="update-form">
          <div className="space-y-2">
            <Label htmlFor="title">Titel *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="z. B. Geplante Wartung Förderband 3"
              required
              className="border-slate-300"
              data-testid="form-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Details zur Anlage, Auswirkungen, Ansprechpartner…"
              rows={5}
              className="border-slate-300"
              data-testid="form-description"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Datum</Label>
              <Input
                id="date"
                type="date"
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                className="border-slate-300"
                data-testid="form-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => update("status", v)}>
                <SelectTrigger className="border-slate-300" data-testid="form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger className="border-slate-300" data-testid="form-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Anlagen</Label>
            <MultiSelectAnlagen
              anlagen={anlagen}
              selectedIds={form.anlage_ids}
              onChange={(ids) => update("anlage_ids", ids)}
              onAnlagenChanged={onAnlagenChanged}
            />
          </div>

          <div className="space-y-2">
            <Label>Anhänge (Bilder & Dateien)</Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-slate-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={onPickFiles}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-slate-300"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Lädt hoch…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Dateien auswählen
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-2">Bilder, PDFs, Dokumente, .exe — alle Dateitypen, mehrere Dateien möglich</p>
            </div>

            {form.attachments.length > 0 && (
              <div className="space-y-1.5 pt-2" data-testid="attachments-list">
                {form.attachments.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 p-2 rounded border border-slate-200 bg-slate-50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {a.is_image ? (
                        <ImageIcon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      ) : (
                        <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      )}
                      <a
                        href={`${FILES_BASE}/${a.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-slate-700 truncate hover:underline"
                      >
                        {a.original_name}
                      </a>
                      <span className="text-xs text-slate-400 flex-shrink-0">{formatBytes(a.size)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`remove-attachment-${a.id}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-300"
              data-testid="form-cancel"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#E2001A] hover:bg-[#C20015] text-white"
              data-testid="form-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichert…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editing ? "Speichern" : "Update erstellen"}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
